import {Box2, Box2Like, Matrix3x2, Vector2} from "@cm/math"
import type {HalContext, HalImageAddressMode, HalPainter, HalPainterImageBlitOptions, HalPainterSourceImage, HalPainterTarget} from "@cm/hal"
import type {HalPainterImageCompositorRef} from "@cm/hal/hal-ref"

export class HalPainterImageStretchTiled implements HalPainter {
    private compositor: HalPainterImageCompositorRef | null

    constructor(readonly context: HalContext) {
        this.compositor = this.context.createHalPainterImageCompositor(COMPOSITING_FUNCTION, "vec4")
    }

    dispose(): void {
        if (!this.compositor) {
            throw new Error("HalPainterImageStretchTiled: Already disposed")
        }
        this.compositor.release()
        this.compositor = null
    }

    paint(target: HalPainterTarget, sourceImage: HalPainterSourceImage, tiledTargetRegion?: Box2Like, options?: HalPainterImageBlitOptions): Box2 {
        if (!this.compositor) {
            throw new Error("HalPainterImageStretchTiled: Already disposed")
        }
        const mipLevel = options?.sourceMipLevel ?? 0
        const {width: imageWidth, height: imageHeight} = sourceImage.descriptor

        // The view transform maps mip-0 texel space → screen space.
        // Its inverse (screenToTexel) maps screen pixel → mip-0 texel.
        const viewTransform = options?.transform ? Matrix3x2.fromMatrix3x2Like(options.transform) : new Matrix3x2()
        const screenToTexel = viewTransform.inverse()

        // Draw region: the tiled region if provided, otherwise the image's screen bounding box.
        const targetRegion = tiledTargetRegion ?? imageScreenBounds(viewTransform, imageWidth, imageHeight)

        this.compositor.ref.paint({
            target,
            sourceImages: sourceImage,
            parameters: {
                u_screenToTexel: {type: "float3x2", value: screenToTexel},
                u_imageSize: {type: "float2", value: new Vector2(imageWidth, imageHeight)},
                u_sourceMipLevel: {type: "int", value: mipLevel},
                u_addressMode: {type: "int", value: halAddressModeToInt(options?.addressMode ?? "clamp")},
            },
            options: {targetRegion},
        })

        return new Box2(0, 0, imageWidth, imageHeight)
    }
}

// Maps screen pixel center to mip-0 texel, then area-weights over the footprint.
const COMPOSITING_FUNCTION = `
    uniform mat3x2 u_screenToTexel;
    uniform vec2 u_imageSize;
    uniform int u_sourceMipLevel;
    uniform int u_addressMode;

    vec4 computeColor(ivec2 targetPixel) {
        float mipLevelScale = 1.0 / float(1 << u_sourceMipLevel);

        // Map screen pixel center to mip-0 texel space.
        // targetPixel is the integer screen coordinate; adding 0.5 gives the pixel center.
        vec2 sourceTexel = u_screenToTexel * vec3(vec2(targetPixel) + vec2(0.5), 1.0);

        // The matrix columns are the exact mip-0 texel step per screen pixel in X and Y.
        // dFdx(u_screenToTexel * screenCoord) = u_screenToTexel[0] (column 0), no quantization.
        vec2 dx = vec2(u_screenToTexel[0]);
        vec2 dy = vec2(u_screenToTexel[1]);

        // Axis-aligned bounding box of the footprint in mip-L space.
        float footprintW = (abs(dx.x) + abs(dy.x)) * mipLevelScale;
        float footprintH = (abs(dx.y) + abs(dy.y)) * mipLevelScale;

        // Tile the source coordinate by wrapping to image bounds.
        vec2 tiledTexel = mod(sourceTexel, u_imageSize);
        vec2 centerL = tiledTexel * mipLevelScale;

        if (footprintW <= 1.0 && footprintH <= 1.0) {
            // Zoomed in or 1:1 — use nearest-point sampling.
            return texelFetchLod0(ivec2(floor(centerL)), u_sourceMipLevel, u_addressMode);
        }

        // Zoomed out — analytically integrate over the screen pixel footprint in mip-L space,
        // weighting each covered texel by its intersection area with the footprint rectangle.
        float x0 = centerL.x - footprintW * 0.5;
        float x1 = centerL.x + footprintW * 0.5;
        float y0 = centerL.y - footprintH * 0.5;
        float y1 = centerL.y + footprintH * 0.5;
        int ix0 = int(floor(x0));
        int ix1 = int(floor(x1));
        int iy0 = int(floor(y0));
        int iy1 = int(floor(y1));
        vec4 result = vec4(0.0);
        float totalWeight = 0.0;
        for (int iy = iy0; iy <= iy1; iy++) {
            for (int ix = ix0; ix <= ix1; ix++) {
                float wx = min(float(ix + 1), x1) - max(float(ix), x0);
                float wy = min(float(iy + 1), y1) - max(float(iy), y0);
                float w = wx * wy;
                result += texelFetchLod0(ivec2(ix, iy), u_sourceMipLevel, u_addressMode) * w;
                totalWeight += w;
            }
        }
        return result / totalWeight;
    }
`

function imageScreenBounds(viewTransform: Matrix3x2, width: number, height: number): Box2 {
    const corners = [
        viewTransform.multiplyVector({x: 0, y: 0}),
        viewTransform.multiplyVector({x: width, y: 0}),
        viewTransform.multiplyVector({x: 0, y: height}),
        viewTransform.multiplyVector({x: width, y: height}),
    ]
    const minX = Math.min(...corners.map((c) => c.x))
    const minY = Math.min(...corners.map((c) => c.y))
    const maxX = Math.max(...corners.map((c) => c.x))
    const maxY = Math.max(...corners.map((c) => c.y))
    return new Box2(minX, minY, maxX - minX, maxY - minY)
}

function halAddressModeToInt(addressMode: HalImageAddressMode): number {
    switch (addressMode) {
        case "clamp":
            return 1
        case "wrap":
            return 2
        case "wrap-mirrored":
            return 3
        case "border":
            return 4
    }
}
